import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, Section, Grid, Card, Text } from '../styles/components';
import { Products, Product, FreshTofuProduct, FriedTofuProduct, SoymilkProduct, TofuCrumblesProduct } from '../types/products';
import { useLocation } from 'react-router-dom';
import TofuCrumblesVarieties from '../components/TofuCrumblesVarieties';
import CoagulationVisualization from '../components/coagulation/CoagulationVisualization';

const HeroSection = styled(Section)`
  position: relative;
  height: 100vh;
  display: flex;
  align-items: center;
  background: url('/images/hero-background.jpg') no-repeat center center;
  background-size: cover;
  color: white;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.05);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 10;
  max-width: 28rem;
  margin: 0 auto;
`;

const HeroLogo = styled.img`
  width: 100%;
  margin-bottom: 2rem;
`;

const HeroText = styled.p`
  font-size: 1.25rem;
  color: var(--text-dark);
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ExploreButton = styled.a`
  display: inline-block;
  background: white;
  color: var(--text-dark);
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  font-size: 1.125rem;
  font-weight: 600;
  transition: background-color 0.3s;

  &:hover {
    background: var(--gray-100);
  }
`;

const ProductsSection = styled(Section)`
  background: var(--gray-50);
  padding: 4rem 0;

  @media (min-width: 768px) {
    padding: 6rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.875rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1rem;
  color: var(--text-dark);

  @media (min-width: 768px) {
    font-size: 2.25rem;
  }
`;

const Divider = styled.div`
  width: 2.5rem;
  height: 0.25rem;
  background-color: var(--primary);
  margin: 0 auto 1.5rem;
  border-radius: 0.25rem;
`;

const TabContainer = styled.div`
  background: var(--gray-50);
  border-radius: 1rem;
  padding: 0.5rem;
  max-width: 48rem;
  margin: 0 auto 3rem;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--gray-100);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 0.25rem;
    margin: 0 auto 2rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  border-radius: 0.75rem;
  font-weight: 500;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  background: ${({ active }) => active ? 'white' : 'transparent'};
  color: ${({ active }) => active ? 'var(--primary)' : 'var(--text-gray)'};
  font-size: 1rem;
  text-align: center;
  box-shadow: ${({ active }) => active ? '0 4px 6px rgba(0, 0, 0, 0.1)' : 'none'};
  white-space: nowrap;
  position: relative;
  overflow: hidden;
  
  ${({ active }) => active && `
    font-weight: 600;
    transform: translateY(-1px);
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0.75rem;
      right: 0.75rem;
      height: 3px;
      background: var(--gray-300);
      border-radius: 3px;
    }
  `}
  
  &:hover {
    ${({ active }) =>
      active
        ? 'background: white; transform: translateY(-1px); box-shadow: 0 6px 8px rgba(0, 0, 0, 0.1);'
        : 'background: var(--gray-100); color: var(--text-dark); transform: translateY(-1px);'}
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    
    ${({ active }) => active && `
      &::after {
        left: 0.5rem;
        right: 0.5rem;
      }
    `}
  }
`;

const ProductGrid = styled(Grid)`
  max-width: 64rem;
  margin: 0 auto;
`;

const ProductCard = styled(Card)`
  overflow: hidden;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 16rem;
  object-fit: cover;
`;

const ProductTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1rem;
`;

const ProductDescription = styled(Text)`
  margin-bottom: 1rem;
`;

const ProductInfo = styled.div`
  padding: 1.5rem;
`;

const InfoCard = styled(Card)`
  margin-bottom: 1.5rem;
`;

const InfoTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 1rem;
`;

const VarietiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  gap: 1.5rem;
  width: 100%;
  max-width: 100%;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const VarietyCard = styled.div<{ color: string }>`
  background-color: ${props => {
    switch (props.color) {
      case 'purple':
        return 'var(--purple-50)';
      case 'red':
        return 'var(--red-50)';
      case 'orange':
        return 'var(--orange-50)';
      case 'green':
        return 'var(--green-50)';
      default:
        return 'var(--orange-50)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.color) {
      case 'purple':
        return 'var(--purple-100)';
      case 'red':
        return 'var(--red-100)';
      case 'orange':
        return 'var(--orange-100)';
      case 'green':
        return 'var(--green-100)';
      default:
        return 'var(--orange-100)';
    }
  }};
  border-radius: 0.5rem;
  padding: 1rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

const VarietyTitle = styled.h5`
  font-size: 1.125rem;
  font-weight: 500;
  color: var(--primary);
  margin-bottom: 0.5rem;
`;

const VarietyDescription = styled.p`
  color: var(--gray-600);
  margin-bottom: 0.75rem;
  flex-grow: 1;
  line-height: 1.5;
`;

const NoticeBox = styled.div<{ color: string }>`
  background: ${({ color }) => color};
  padding: 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-dark);
`;

const BenefitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BenefitItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 0.5rem;
  color: var(--text-gray);

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: var(--green-500);
    margin-right: 0.5rem;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
`;

const OtaDifferenceSection = styled(Section)`
  background: var(--gray-50);
  padding: 4rem 0;

  @media (min-width: 768px) {
    padding: 6rem 0;
  }
`;

const DifferenceTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 800;
  text-align: center;
  color: var(--text-dark);
  margin-bottom: 0.5rem;

  @media (min-width: 768px) {
    font-size: 3rem;
  }
`;

const DifferenceDivider = styled(Divider)`
  width: 3rem;
  margin-bottom: 2rem;
`;

const DifferenceGrid = styled(Grid)`
  max-width: 64rem;
  margin: 0 auto;
`;

const DifferenceCard = styled(Card)`
  padding: 2rem;
  transition: all 0.3s;
  transform: translateY(0);

  &:hover {
    transform: translateY(-0.25rem);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  }
`;

const IconWrapper = styled.div<{ color: string }>`
  width: 4rem;
  height: 4rem;
  border-radius: 9999px;
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;

  svg {
    width: 2rem;
    height: 2rem;
    color: ${({ color }) => color.replace('50', '500')};
  }
`;

const FAQSection = styled(Section)`
  background: var(--gray-50);
  padding: 4rem 0;
`;

const FAQContainer = styled.div`
  max-width: 48rem;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 1rem;
`;

const FAQHeader = styled.div`
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const FAQQuestion = styled.h3`
  font-size: 1.25rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const FAQContent = styled.div<{ isOpen: boolean }>`
  padding: 1.25rem;
  border-top: 1px solid var(--border);
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const FAQList = styled.ul`
  margin-top: 1rem;
  margin-left: 1.5rem;
  list-style-type: disc;
`;

const FAQListItem = styled.li`
  color: var(--text-gray);
  margin-bottom: 0.5rem;
`;

const ContactCTA = styled(Card)`
  max-width: 42rem;
  margin: 3rem auto 0;
  text-align: center;
  padding: 2rem;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 0.75rem 2rem;
  background: var(--primary);
  color: white;
  border-radius: 0.375rem;
  font-weight: 500;
  text-decoration: none;
  transition: all 0.3s;
  
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-1px);
  }
`;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0.75rem 2rem;
  background-color: var(--primary);
  color: white;
  font-weight: 600;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: var(--gray-700);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const Home: React.FC = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<keyof Products>('fresh');
  const [openFAQ, setOpenFAQ] = useState<string | null>(null);
  const [showCoagulation, setShowCoagulation] = useState(false);

  useEffect(() => {
    // Handle hash navigation to products section
    if (location.hash === '#products') {
      const productsSection = document.getElementById('products');
      if (productsSection) {
        setTimeout(() => {
          const headerOffset = 80; // Height of the header
          const elementPosition = productsSection.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }, 100);
      }
    }

    // Handle FAQ opening from URL
    if (location.hash.startsWith('#faq-')) {
      const faqIndex = parseInt(location.hash.replace('#faq-', ''));
      if (!isNaN(faqIndex) && faqs[faqIndex]) {
        setOpenFAQ(faqs[faqIndex].question);
        
        // Scroll to the FAQ section
        const faqSection = document.getElementById('faq');
        if (faqSection) {
          setTimeout(() => {
            const headerOffset = 80;
            const elementPosition = faqSection.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }, 100);
        }
      }
    }
  }, [location.hash]);

  const products: Products = {
    fresh: {
      title: 'Fresh Handmade Tofu',
      description: 'Our traditional tofu is handcrafted daily using non-GMO soybeans and traditional nigari coagulant. Available in four textures to suit your culinary needs.',
      image: '/images/fresh-handmade-tofu.webp',
      info: {
        title: 'Why Handmade Matters',
        description: 'Unlike mass-produced tofu, our handcrafted process using nigari creates a delicate, intricate protein network with superior texture and flavor that machines simply cannot replicate.',
        additional: 'Our tofu is made fresh daily, allowing you to experience the most authentic texture and clean, fresh taste of traditional tofu.'
      },
      benefits: [
        'High in protein and essential amino acids',
        'Rich in calcium and iron',
        'Low in calories, high in nutrients',
        'Naturally cholesterol-free'
      ],
      varieties: [
        {
          title: 'Soft Tofu',
          description: 'Our silky soft tofu has a delicate, custard-like texture. Perfect for soups, smoothies, and Asian desserts.',
          color: 'purple'
        },
        {
          title: 'Medium Tofu',
          description: 'Our medium tofu balances firmness and softness. Versatile for recipes requiring both tenderness and shape retention.',
          color: 'red'
        },
        {
          title: 'Firm Tofu',
          description: 'Our firm tofu holds its shape well during cooking. Perfect for stir-fries, grilling, and baking.',
          color: 'orange'
        },
        {
          title: 'Extra Firm Tofu',
          description: 'Our extra firm tofu has the highest protein content and maintains its shape even under high heat. Ideal for grilling, baking, and hearty dishes.',
          color: 'green'
        }
      ]
    },
    crumbles: {
      title: 'Tofu Crumbles',
      description: 'Our versatile, all natural, minimally processed tofu crumbles are perfect for adding plant-based protein to your favorite dishes. Available in three delicious flavors to enhance any meal.',
      image: '/images/tofu-crumbles.webp',
      info: {
        title: 'Versatile Plant-Based Protein',
        description: 'Our tofu crumbles are made from our premium firm tofu and seasoned to perfection, offering a convenient way to add protein to your meals.',
        additional: 'Every 2.2oz of tofu crumble is equal to 1oz of alternate protein and are perfect for tacos, pasta, salads, and more - our crumbles are ready to use and packed with flavor.'
      },
      benefits: [
        'High in plant-based protein',
        'Ready to use - minimal preparation needed',
        'Versatile for many recipes',
        'Naturally cholesterol-free'
      ],
      varieties: [
        {
          title: 'Taco Crumbles',
          description: 'Perfect for tacos, burritos, and Mexican-inspired dishes. Seasoned with traditional taco spices.',
          color: 'red',
          nutritionImage: '/images/nutrition-taco.webp',
          ingredients: 'INGREDIENTS: Tofu(Water, non-GMO Soybeans, Magnesium Chloride Nigari), Canola Oil, Ancho Peppers, Garlic, Cumin, Onion, Coriander, Salt, Paprika, Black Pepper, Smoked Paprika, Sugar, Oregano, Citric Acid',
          allergens: 'CONTAINS: Soy'
        },
        {
          title: 'Italian Crumbles',
          description: 'Ideal for pasta dishes, pizza toppings, and Italian recipes. Infused with Italian herbs and spices.',
          color: 'green',
          nutritionImage: '/images/nutrition-italian.webp',
          ingredients: 'INGREDIENTS: Tofu (Water, non-GMO Soybeans, Magnesium Chloride Nigari), Canola Oil, Garlic, Oregano, Tomato, Rosemary, Basil, Thyme, Black Pepper, Salt, Parsley, Citric Acid',
          allergens: 'CONTAINS: Soy'
        },
        {
          title: 'Breakfast Sausage Crumbles',
          description: 'Great for breakfast scrambles, sandwiches, and morning meals. Seasoned with traditional breakfast sausage flavors.',
          color: 'orange',
          nutritionImage: '/images/nutrition-breakfast.webp',
          ingredients: 'INGREDIENTS: Tofu (Water, non-GMO Soybeans, Magnesium Chloride Nigari), Canola Oil, Brown Sugar, Fennel Seeds, Thyme, Yeast Extract, Salt, Black Pepper, Garlic, Paprika, Chili Flakes',
          allergens: 'CONTAINS: Soy'
        },
        {
          title: 'Asian Crumbles',
          description: 'A savory blend of garlic, ginger, and tamari perfect for stir-fries, fried rice, and lettuce wraps.',
          color: 'purple',
          nutritionImage: '/images/nutrition-asian.webp',
          ingredients: 'INGREDIENTS: Tofu(Water, non-GMO Soybeans, Magnesium Chloride Nigari), Canola Oil, Tamari Powder(Tamari Soy Sauce [Soybeans, Salt, Sugar], Maltodextrin, Salt), Cane Sugar, Garlic, White Pepper',
          allergens: 'CONTAINS: Soy'
        }
      ]
    },
    fried: {
      title: 'Fried Tofu "Agé"',
      description: 'Our golden, deep-fried tofu come in two varieties: Agé and Atsu Agé (thicker and more dense interior).',
      image: '/images/fried-tofu-age.webp',
      notice: 'Agé is pre-cooked for convenience. Simply slice or use whole in your favorite recipes.',
      info: {
        title: 'About Agé',
        description: 'Agé (pronounced "ah-gay") is a traditional Japanese ingredient featuring tofu that\'s been deep-fried until golden brown. The frying process creates a unique texture – crispy on the outside and a spongy interior that readily absorbs flavors.',
        additional: 'Our Agé is made from our premium firm tofu and is fried fresh to ensure the best quality and taste.',
      },
    },
    soymilk: {
      title: 'Fresh Soymilk',
      description: 'Our pure, fresh soymilk is made daily using the same high-quality soybeans we use for our tofu. With no additives or preservatives, you\'ll taste the difference immediately.',
      image: '/images/fresh-soymilk.webp',
      notice: 'Our soymilk should be kept refrigerated and consumed within 5 days for optimal freshness.',
      info: {
        title: 'The Ota Soymilk Difference',
        description: 'Unlike commercial soymilk that often contains additives and sweeteners, our soymilk is pure and simple – just soybeans and water. The result is a clean, authentic flavor that truly showcases the natural richness of our premium soybeans.',
        additional: 'Many customers tell us they\'ve never tasted soymilk like ours before – and once they try it, they can\'t go back to store brands!',
      },
      benefits: [
        'High in plant-based protein',
        'Contains all essential amino acids',
        'Naturally lactose-free',
        'No added sugars or preservatives',
      ],
    },
  };

  const getActiveProduct = (): Product => {
    return products[activeTab];
  };

  const isFriedTofu = (product: Product): product is FriedTofuProduct => {
    return 'notice' in product && !('varieties' in product);
  };

  const isFreshTofu = (product: Product): product is FreshTofuProduct => {
    return product.title === 'Fresh Handmade Tofu';
  };

  const isSoymilk = (product: Product): product is SoymilkProduct => {
    return 'notice' in product && 'benefits' in product && !('varieties' in product);
  };

  const isTofuCrumbles = (product: Product): product is TofuCrumblesProduct => {
    return product.title === 'Tofu Crumbles';
  };

  const differences = [
    {
      title: 'Traditional Coagulant',
      description: 'Ota Tofu is made with only three ingredients – soybeans, water, and nigari. This traditional coagulant, derived from seawater and imported from Japan, is one reason our tofu must be handmade.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M2 10c1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0 1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0" />
          <path d="M2 14c1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0 1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0" />
          <path d="M2 18c1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0 1.5-2 3.5-2 5 0 1.5 2 3.5 2 5 0" />
        </svg>
      ),
      color: 'var(--blue-50)',
    },
    {
      title: 'Delicate Process',
      description: 'Nigari produces soybean curds that are too delicate for a machine to process, requiring each slab of Ota Tofu to be hand stirred and handcrafted.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
        </svg>
      ),
      color: 'var(--red-50)',
    },
    {
      title: 'Superior Result',
      description: 'While other producers use calcium sulfate (gypsum) that allows for machine processing, we believe nigari creates better texture and flavor – and invite you to taste the difference.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
      ),
      color: 'var(--purple-50)',
    },
  ];

  const faqs = [
    {
      question: 'How long does Ota Tofu stay fresh?',
      answer: 'Our fresh tofu should be used within 5-7 days of purchase for optimal quality. Keep refrigerated and store opened packages in water, changing the water daily for best results.',
    },
    {
      question: 'Where can I buy Ota Tofu products?',
      answer: 'If you are local to Portland, feel free to drop by the original Ota Tofu building, located at 812 SE Start St. Portland, OR 97214. You can also find us at many of your local grocery stores, including (but not limited to):',
      list: [
        'New Seasons',
        'Uwajimaya',
        'Market Of Choice',
        'H-MART',
        'G-MART',
        'Sheridan Fruit',
        'Lily Market',
        'Fubonn',
        'Boo Han',
        'Gobugi',
        'Food Fight Grocery',
        'Wing Wa Oriental Market (Salem)',
        'Rice \'N\' Spice Oriental Foods (Corvallis)',
        'Sunrise Asian Food Market (Eugene)',
        'Town & Country Markets (Greater Seattle Area)',
      ],
    },
    {
      question: 'What makes Ota Tofu different from other brands?',
      answer: 'Ota Tofu is handmade using the highest quality ingredients and traditional methods and nigari as a coagulant, which creates a superior texture and flavor compared to mass-produced tofu that uses calcium sulfate and machine processing. Learn more about us here.',
    },
    {
      question: 'Where can I get Ota Tofu Merch?',
      answer: 'Check out our merch store at tofumerch.com!',
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <HeroSection>
        <Container>
          <HeroContent>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <HeroLogo src="/images/otatofuamericasoldest.png" alt="Ota Tofu - America's Oldest Tofu Manufacturer" />
              <HeroText>Handcrafted tofu since using traditional methods since 1911</HeroText>
              <ExploreButton href="#products">Explore Our Products</ExploreButton>
            </motion.div>
          </HeroContent>
        </Container>
      </HeroSection>

      {/* Products Section */}
      <ProductsSection id="products">
        <Container>
          <DifferenceTitle>OUR PRODUCTS</DifferenceTitle>
          <DifferenceDivider />
          <Text style={{ textAlign: 'center', maxWidth: '48rem', margin: '0 auto 3rem' }}>
            Each variety of our tofu is crafted with care to provide the perfect texture and taste for specific culinary applications.
          </Text>

          {/* Product Tabs */}
          <TabContainer>
            <TabButton
              active={activeTab === 'fresh'}
              onClick={() => setActiveTab('fresh')}
            >
              Fresh Tofu
            </TabButton>
            <TabButton
              active={activeTab === 'crumbles'}
              onClick={() => setActiveTab('crumbles')}
            >
              Tofu Crumbles
            </TabButton>
            <TabButton
              active={activeTab === 'fried'}
              onClick={() => setActiveTab('fried')}
            >
              Fried Tofu
            </TabButton>
            <TabButton
              active={activeTab === 'soymilk'}
              onClick={() => setActiveTab('soymilk')}
            >
              Soymilk
            </TabButton>
          </TabContainer>

          {/* Product Details */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ProductGrid columns={2}>
              {/* Main Product Card */}
              <ProductCard>
                <ProductImage
                  src={getActiveProduct().image}
                  alt={getActiveProduct().title}
                />
                <ProductInfo>
                  <ProductTitle>{getActiveProduct().title}</ProductTitle>
                  <ProductDescription>{getActiveProduct().description}</ProductDescription>
                  {(isFriedTofu(getActiveProduct()) || isSoymilk(getActiveProduct())) && (
                    <NoticeBox color="var(--yellow-50)">
                      {(getActiveProduct() as FriedTofuProduct | SoymilkProduct).notice}
                    </NoticeBox>
                  )}
                </ProductInfo>
              </ProductCard>

              {/* Product Info Cards */}
              <div>
                <InfoCard>
                  <InfoTitle>{getActiveProduct().info.title}</InfoTitle>
                  <Text>{getActiveProduct().info.description}</Text>
                  <Text>{getActiveProduct().info.additional}</Text>
                </InfoCard>

                {(isFreshTofu(getActiveProduct()) || isSoymilk(getActiveProduct())) && (
                  <InfoCard>
                    <InfoTitle>Nutritional Benefits</InfoTitle>
                    <BenefitList>
                      {(getActiveProduct() as FreshTofuProduct | SoymilkProduct).benefits.map((benefit: string, index: number) => (
                        <BenefitItem key={index}>
                          <svg fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span>{benefit}</span>
                        </BenefitItem>
                      ))}
                    </BenefitList>
                  </InfoCard>
                )}
              </div>

              {/* Varieties Grid (if applicable) */}
              {isFreshTofu(getActiveProduct()) && (
                <div style={{ gridColumn: '1 / -1', marginTop: '2rem' }}>
                  <InfoCard>
                    <InfoTitle style={{ textAlign: 'center', marginBottom: '1.5rem' }}>Our Tofu Varieties</InfoTitle>
                    <VarietiesGrid>
                      {(getActiveProduct() as FreshTofuProduct).varieties.map((variety: { title: string; description: string; color: string }, index: number) => (
                        <VarietyCard key={index} color={variety.color}>
                          <VarietyTitle>{variety.title}</VarietyTitle>
                          <VarietyDescription>{variety.description}</VarietyDescription>
                        </VarietyCard>
                      ))}
                    </VarietiesGrid>
                  </InfoCard>
                </div>
              )}

              {isTofuCrumbles(getActiveProduct()) && (
                <TofuCrumblesVarieties varieties={(getActiveProduct() as TofuCrumblesProduct).varieties} />
              )}
            </ProductGrid>
          </motion.div>
        </Container>
      </ProductsSection>

      {/* The Ota Difference Section */}
      <OtaDifferenceSection>
        <Container>
          <DifferenceTitle>THE OTA DIFFERENCE</DifferenceTitle>
          <DifferenceDivider />
          <Text style={{ textAlign: 'center', maxWidth: '48rem', margin: '0 auto 4rem' }}>
            What makes Ota Tofu stand out from other brands? Our dedication to traditional craftsmanship and quality ingredients creates a tofu experience unlike any other.
          </Text>

          <DifferenceGrid columns={3}>
            {differences.map((difference, index) => (
              <DifferenceCard key={index}>
                <IconWrapper color={difference.color}>
                  {difference.icon}
                </IconWrapper>
                <InfoTitle>{difference.title}</InfoTitle>
                <Text>{difference.description}</Text>
              </DifferenceCard>
            ))}
          </DifferenceGrid>

          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <ToggleButton onClick={() => setShowCoagulation(!showCoagulation)}>
              {showCoagulation ? 'Hide Coagulation Science' : 'More about Coagulation Science'}
            </ToggleButton>
          </div>

          {showCoagulation && (
            <div className="mt-8">
              <CoagulationVisualization />
            </div>
          )}
        </Container>
      </OtaDifferenceSection>

      {/* FAQ Section */}
      <FAQSection id="faq">
        <Container>
          <DifferenceTitle>Frequently Asked Questions</DifferenceTitle>
          <DifferenceDivider />
          <Text style={{ textAlign: 'center', maxWidth: '48rem', margin: '0 auto 3rem' }}>
            Get answers to common questions about our tofu products.
          </Text>

          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index} id={`faq-${index}`}>
                <FAQHeader onClick={() => setOpenFAQ(openFAQ === faq.question ? null : faq.question)}>
                  <FAQQuestion>{faq.question}</FAQQuestion>
                  <svg
                    style={{
                      width: '1.25rem',
                      height: '1.25rem',
                      color: 'var(--text-gray)',
                      transform: openFAQ === faq.question ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.3s ease'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </FAQHeader>
                <FAQContent isOpen={openFAQ === faq.question}>
                  <Text>{faq.answer}</Text>
                  {faq.list && (
                    <FAQList>
                      {faq.list.map((item, i) => (
                        <FAQListItem key={i}>{item}</FAQListItem>
                      ))}
                    </FAQList>
                  )}
                </FAQContent>
              </FAQItem>
            ))}
          </FAQContainer>

          <ContactCTA>
            <Text style={{ fontSize: '1.125rem', marginBottom: '1.5rem' }}>
              Have more questions about our products?
            </Text>
            <CTAButton href="/contact">Contact Us</CTAButton>
          </ContactCTA>
        </Container>
      </FAQSection>
    </div>
  );
};

export default Home; 
 
 